import { useEffect, useRef } from 'react';
// The CSP build, with its worker served as a real file rather than built from
// an inline blob. This is not about CSP here — it is about the build. The
// default MapLibre bundle hands the worker a script it assembles at runtime
// from its own source, and Create React App's minifier renames identifiers that
// script depends on, so the worker throws "i is not defined" the moment it is
// asked to set the style's layers. It never builds its layer index, and from
// then on every tile fails with "Cannot read properties of undefined (reading
// 'basemap')" and the map stays empty. A worker loaded from a file webpack
// never touched has no such problem.
import maplibregl from 'maplibre-gl/dist/maplibre-gl-csp';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';

maplibregl.setWorkerUrl('/maplibre-gl-csp-worker.js');

// Where the register's incidents actually happened.
//
// The basemap is a single PMTiles archive of the UK served by our own nginx
// (/tiles/uk.pmtiles) and read by the browser over HTTP range requests. There
// is no tile server and nothing new on the routing box — OSRM holds England's
// road graph in memory there and its swap is already load-bearing.
//
// No text is drawn from the vector tiles, so the map needs no glyph server
// either; the handful of city names below are plain HTML markers, and every
// pin carries its own address in the popup.
const BRAND = '#711C91';
const TILES = '/tiles/uk.pmtiles';

// Registered once, at module scope, before any map exists. Two details matter:
// `tilev4` is the promise-based handler MapLibre 4 expects — the plain `tile`
// export is the old callback shape for MapLibre 3, and handing that to v4
// throws before a single byte is requested, leaving a blank map and an error
// with no message. And the protocol is never removed: tearing it down in an
// effect cleanup would pull it out from under a map that is still mounted.
const protocol = new Protocol();
maplibregl.addProtocol('pmtiles', protocol.tilev4);

// England and Wales, where the work is. The map still pans to Scotland.
const UK_VIEW = { center: [-2.4, 53.2], zoom: 5.4 };

// Enough to orient someone without a label server. Kept short on purpose —
// these are for "roughly where", the pin popup gives the address.
const CITIES = [
  ['London', -0.128, 51.507], ['Birmingham', -1.898, 52.489], ['Manchester', -2.244, 53.483],
  ['Leeds', -1.549, 53.801], ['Liverpool', -2.983, 53.408], ['Sheffield', -1.470, 53.381],
  ['Bristol', -2.587, 51.455], ['Nottingham', -1.150, 52.954], ['Newcastle', -1.614, 54.978],
  ['Leicester', -1.133, 52.637], ['Bradford', -1.753, 53.795], ['Preston', -2.699, 53.759],
  ['Norwich', 1.298, 52.630], ['Southampton', -1.404, 50.910], ['Cardiff', -3.179, 51.481],
  ['Plymouth', -4.143, 50.376], ['Hull', -0.336, 53.745], ['Stoke', -2.177, 53.002],
  ['Derby', -1.476, 52.922], ['Coventry', -1.510, 52.407], ['Ipswich', 1.155, 52.059],
  ['Maidstone', 0.522, 51.272], ['Glasgow', -4.252, 55.864], ['Edinburgh', -3.188, 55.953],
];

// A quiet basemap: the pins are the subject, so the land, water and roads sit
// well back and nothing under a pin competes with it for attention.
const style = {
  version: 8,
  // Addressed through the archive's TileJSON (`url`) rather than an inline
  // tile template, and that is load-bearing. Style._load adds sources BEFORE
  // _createLayers broadcasts the layer list to the worker; an inline template
  // makes the source ready immediately, so a tile can reach the worker while
  // its layer index is still empty and every tile fails with an undefined
  // familiesBySource. The TileJSON fetch is what keeps the two in order.
  sources: {
    basemap: {
      type: 'vector',
      url: `pmtiles://${window.location.origin}${TILES}`,
      attribution: 'OpenStreetMap · Protomaps',
    },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#b9cbd9' } },
    {
      id: 'earth', type: 'fill', source: 'basemap', 'source-layer': 'earth', paint: { 'fill-color': '#ece8e1' },
    },
    {
      id: 'landuse',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landuse',
      filter: ['in', ['get', 'kind'], ['literal', ['park', 'forest', 'wood', 'nature_reserve', 'grass', 'farmland', 'scrub']]],
      paint: { 'fill-color': '#dde5d4' },
    },
    {
      id: 'water', type: 'fill', source: 'basemap', 'source-layer': 'water', paint: { 'fill-color': '#b9cbd9' },
    },
    {
      id: 'buildings',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'buildings',
      minzoom: 13,
      paint: { 'fill-color': '#ddd8d0' },
    },
    {
      id: 'roads-minor',
      type: 'line',
      source: 'basemap',
      'source-layer': 'roads',
      minzoom: 11,
      filter: ['in', ['get', 'kind'], ['literal', ['minor_road', 'medium_road']]],
      paint: {
        'line-color': '#d8d1c8',
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 16, 4],
      },
    },
    {
      id: 'roads-major',
      type: 'line',
      source: 'basemap',
      'source-layer': 'roads',
      filter: ['in', ['get', 'kind'], ['literal', ['highway', 'major_road']]],
      paint: {
        // Warm grey rather than white: at the zoom this map opens on, white
        // roads on near-white land are the reason a working map can look like
        // an empty box.
        'line-color': '#c9c0b6',
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.6, 10, 2, 16, 7],
      },
    },
    {
      id: 'boundaries',
      type: 'line',
      source: 'basemap',
      'source-layer': 'boundaries',
      paint: { 'line-color': '#a99fb0', 'line-dasharray': [2, 2], 'line-width': 0.9 },
    },
  ],
};

// Open records carry the brand colour because they are the ones that still
// need someone; closed ones fade back without disappearing, so a cluster of
// past incidents at one address still reads as a cluster.
const OPEN = BRAND;
const CLOSED = '#b9adc2';

export default function CxRegisterMap({
  pins, selectedId, onSelect, height = 460, closedStatuses = [],
}) {
  const holder = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const ready = useRef(false);

  useEffect(() => {
    if (map.current || !holder.current) return undefined;
    const m = new maplibregl.Map({
      container: holder.current, style, ...UK_VIEW, attributionControl: false,
    });
    map.current = m;
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    m.addControl(new maplibregl.AttributionControl({
      compact: true, customAttribution: 'OpenStreetMap · Protomaps',
    }), 'bottom-right');

    // A map that fails silently looks exactly like a map with nothing on it,
    // so say which one it is.
    m.on('error', (e) => console.error('[cx-map]', (e && e.error && e.error.message) || e,
      e && e.sourceId ? `source=${e.sourceId}` : ''));

    m.on('load', () => {
      m.addSource('incidents', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addLayer({
        id: 'incident-halo',
        type: 'circle',
        source: 'incidents',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 9, 12, 16],
          'circle-color': OPEN,
          'circle-opacity': 0.14,
        },
      });
      m.addLayer({
        id: 'incidents',
        type: 'circle',
        source: 'incidents',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 4.5, 12, 8],
          'circle-color': ['case', ['get', 'isOpen'], OPEN, CLOSED],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        },
      });
      m.addLayer({
        id: 'incident-selected',
        type: 'circle',
        source: 'incidents',
        filter: ['==', ['get', 'recordId'], -1],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 8, 12, 13],
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#111827',
        },
      });

      CITIES.forEach(([name, lon, lat]) => {
        const el = document.createElement('span');
        el.textContent = name;
        el.style.cssText = 'font:600 11px system-ui,sans-serif;color:#5d5766;'
          + 'text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;pointer-events:none;white-space:nowrap';
        new maplibregl.Marker({ element: el, anchor: 'left' }).setLngLat([lon, lat]).addTo(m);
      });

      ready.current = true;
      m.fire('nano.ready');
    });

    m.on('mouseenter', 'incidents', () => { m.getCanvas().style.cursor = 'pointer'; });
    m.on('mouseleave', 'incidents', () => { m.getCanvas().style.cursor = ''; });
    m.on('click', 'incidents', (e) => {
      const f = e.features && e.features[0];
      if (f && onSelect) onSelect(Number(f.properties.recordId));
    });

    return () => { m.remove(); map.current = null; ready.current = false; };
    // onSelect is read through the closure above; remounting the map on every
    // parent render would throw away the user's pan and zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pins follow the page's filters, so the map always shows the same set as
  // the list beneath it.
  useEffect(() => {
    const m = map.current;
    if (!m) return undefined;
    const apply = () => {
      const src = m.getSource('incidents');
      if (!src) return;
      const features = (pins || []).map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [Number(p.longitude), Number(p.latitude)] },
        properties: {
          recordId: p.id,
          isOpen: !closedStatuses.includes(p.status),
          reference: p.reference || `#${p.sp_item_id}`,
          status: p.status || '',
          category: p.category || p.event_type || '',
          address: p.address || '',
          reported: p.reported_at ? new Date(p.reported_at).toLocaleDateString('en-GB') : '',
        },
      }));
      src.setData({ type: 'FeatureCollection', features });

      if (features.length) {
        const b = new maplibregl.LngLatBounds();
        features.forEach((f) => b.extend(f.geometry.coordinates));
        m.fitBounds(b, { padding: 60, maxZoom: 12, duration: 400 });
      }
    };
    if (ready.current) apply(); else m.once('nano.ready', apply);
    return undefined;
  }, [pins, closedStatuses]);

  // Opening a record from the list ringmarks it and brings up its popup, so
  // the two halves of the page point at the same thing.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready.current) return undefined;
    if (m.getLayer('incident-selected')) {
      m.setFilter('incident-selected', ['==', ['get', 'recordId'], selectedId == null ? -1 : selectedId]);
    }
    if (popup.current) { popup.current.remove(); popup.current = null; }
    const hit = (pins || []).find((p) => p.id === selectedId);
    if (!hit) return undefined;
    popup.current = new maplibregl.Popup({ closeButton: true, offset: 12, maxWidth: '260px' })
      .setLngLat([Number(hit.longitude), Number(hit.latitude)])
      .setHTML(`<div style="font:400 12px system-ui,sans-serif;line-height:1.45">
          <strong style="color:${BRAND}">${escapeHtml(hit.reference || '')}</strong><br>
          ${escapeHtml(hit.category || hit.event_type || '')}<br>
          <span style="color:#6b7280">${escapeHtml(hit.address || '')}</span>
        </div>`)
      .addTo(m);
    m.easeTo({ center: [Number(hit.longitude), Number(hit.latitude)], zoom: Math.max(m.getZoom(), 11), duration: 500 });
    return undefined;
  }, [selectedId, pins]);

  return <div ref={holder} style={{ height }} className="w-full" />;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
